import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SharedHeader from "@/components/SharedHeader";
import { useAuth } from "@/contexts/AuthContext";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { TURNSTILE_SITE_KEY } from "@/lib/env";
import logoUrl from "@/logo.png";

// Simple email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js";
let turnstileScriptPromise: Promise<void> | null = null;

const loadTurnstileScript = (): Promise<void> => {
  if (window.turnstile) {
    return Promise.resolve();
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("turnstile-script");
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Turnstile failed to load")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = "turnstile-script";
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
};

// Cloudflare Turnstile types
declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'error-callback'?: () => void;
      }) => string;
      ready: (callback: () => void) => void;
      reset: (widgetId: string) => void;
    };
  }
}

interface EmailFormProps {
  onSubmit: (email: string, captchaToken?: string) => Promise<void>;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  mode?: 'login' | 'signup';
}


function EmailForm({ onSubmit, inputRef, mode = 'signup' }: EmailFormProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [validationError, setValidationError] = useState("");
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaInitialized, setCaptchaInitialized] = useState(false);
  const captchaContainerRef = useRef<HTMLDivElement>(null);
  const captchaWidgetIdRef = useRef<string | null>(null);

  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setValidationError("Please enter your email address");
      return false;
    }
    if (!EMAIL_REGEX.test(value)) {
      setValidationError("Please enter a valid email address");
      return false;
    }
    setValidationError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if captcha is required but not completed
    if (captchaInitialized && !captchaToken && !showCaptcha) {
      // If captcha was initialized but token is missing, show captcha again
      setShowCaptcha(true);
      if (captchaWidgetIdRef.current && window.turnstile) {
        window.turnstile.reset(captchaWidgetIdRef.current);
      }
      return;
    }

    if (!validateEmail(email)) {
      return;
    }

    setIsLoading(true);
    setFeedback(null);

    try {
      await onSubmit(email, captchaToken || undefined);
      setFeedback({
        type: "success",
        message: mode === 'login'
          ? `Check your inbox at ${email} for your login link. If you don't see it, check your spam folder.`
          : `Check your inbox at ${email} to activate your account and start using PodWise GPT! If you don't see it, check your spam folder.`
      });
      setEmail(""); // Clear email on success
      // Reset captcha after successful submission
      setCaptchaToken(null);
      setCaptchaInitialized(false);
      if (captchaWidgetIdRef.current && window.turnstile) {
        window.turnstile.reset(captchaWidgetIdRef.current);
      }
    } catch (error) {
      setFeedback({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to send magic link. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setValidationError("");
    setFeedback(null);
  };

  // Initialize captcha when email input is focused/clicked
  const handleEmailFocus = () => {
    if (captchaInitialized) {
      // Already initialized, don't do anything
      return;
    }

    setCaptchaInitialized(true);
    setShowCaptcha(true);

    // Function to render captcha
    const renderCaptcha = () => {
      // Double-check container exists and Turnstile is available
      if (!captchaContainerRef.current || !window.turnstile) {
        return;
      }

      // Check if container already has a Turnstile widget
      if (captchaContainerRef.current.querySelector('[data-cf-turnstile-widget-id]')) {
        // Widget already exists, don't render again
        return;
      }

      try {
        const widgetId = window.turnstile.render(captchaContainerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            setCaptchaToken(token);
            setShowCaptcha(false);
          },
          'error-callback': () => {
            // On error, still allow submission but without token
            setShowCaptcha(false);
          },
        });
        captchaWidgetIdRef.current = widgetId;
      } catch (error) {
        console.error('Turnstile render error:', error);
        setShowCaptcha(false);
      }
    };

    // Check if Turnstile is already loaded
    const renderWhenReady = () => {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (window.turnstile?.ready) {
          window.turnstile.ready(renderCaptcha);
        } else {
          renderCaptcha();
        }
      }, 100);
    };

    if (window.turnstile) {
      renderWhenReady();
      return;
    }

    loadTurnstileScript()
      .then(renderWhenReady)
      .catch(() => {
        console.warn("Turnstile failed to load");
        setShowCaptcha(false);
      });
  };

  const handleEmailClick = handleEmailFocus;

  return (
    <div className="flex flex-col gap-3 w-full">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              ref={inputRef}
              type="email"
              value={email}
              onChange={handleEmailChange}
              onFocus={handleEmailFocus}
              onClick={handleEmailClick}
              placeholder="Type your email..."
              className={cn(
                "w-full px-4 py-3 text-base rounded-xl",
                "bg-white dark:bg-gray-900",
                theme.borders.neubrutalism,
                theme.shadows.neubrutalismSm,
                "focus:outline-none focus:ring-4",
                theme.ring.primaryOrange,
                validationError && "border-red-500 dark:border-red-400"
              )}
              disabled={isLoading}
            />
            {validationError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {validationError}
              </p>
            )}
          </div>
          <Button
            type="submit"
            disabled={isLoading}
            className={cn(
              theme.buttons.primary,
              theme.animations.buttonBlinking,
              "px-6 py-3 font-semibold rounded-xl whitespace-nowrap",
              showCaptcha && "hidden"
            )}
          >
            {isLoading ? "Sending..." : mode === 'login' ? "Send Login Link" : "Get Started"}
          </Button>
        </div>
        {/* Cloudflare Turnstile Captcha Container */}
        <div
          ref={captchaContainerRef}
          className={cn(
            "flex justify-center mt-6 mb-2",
            showCaptcha ? "block" : "hidden"
          )}
        />
      </form>

      {feedback && (
        <div
          className={cn(
            "p-4 rounded-xl text-base font-semibold",
            feedback.type === "success"
              ? cn(
                  theme.colors.cardCream,
                  theme.borders.neubrutalism,
                  theme.shadows.neubrutalismSm,
                  "text-gray-900 dark:text-white"
                )
              : "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border-3 border-red-600 dark:border-red-400"
          )}
        >
          {feedback.type === "success" ? (
            <div className="space-y-1">
              <p className="font-bold">{feedback.message}</p>
              <p className="text-sm font-normal text-gray-700 dark:text-gray-300 mt-2">
                If you don&apos;t receive anything check your spam folder.
              </p>
            </div>
          ) : (
            <span>{feedback.message}</span>
          )}
        </div>
      )}
    </div>
  );
}

interface StickyBottomBarProps {
  visible: boolean;
  onSubmit: (email: string) => Promise<void>;
}

function StickyBottomBar({ visible, onSubmit }: StickyBottomBarProps) {
  const stickyEmailRef = useRef<HTMLInputElement>(null);

  if (!visible) return null;

  return (
    <div className={cn(
      "fixed bottom-0 left-0 right-0 z-50",
      "transform transition-transform duration-300",
      visible ? "translate-y-0" : "translate-y-full",
      theme.colors.cardCream,
      "border-t-3 border-gray-900 dark:border-gray-300",
      theme.shadows.neubrutalismLg
    )}>
      <div className="mx-auto max-w-7xl px-6 py-5">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="hidden md:block">
            <h3 className="font-black text-lg text-gray-900 dark:text-white">
              Ready to try it?
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Chat with any podcast · Just paste an RSS feed · Instant access
            </p>
          </div>
          <div className="flex-1 max-w-md w-full sm:w-auto">
            <EmailForm onSubmit={onSubmit} inputRef={stickyEmailRef} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [loginMode, setLoginMode] = useState<'login' | 'signup'>('signup');
  const emailInputRef = useRef<HTMLInputElement>(null);
  // Redirect authenticated users to dashboard or paywall
  const { requestMagicLink, isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      if (user.subscription_status === "active") {
        navigate('/episodes');
      } else {
        navigate('/paywall');
      }
    }
  }, [isAuthenticated, isLoading, navigate, user]);

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky bar after scrolling past hero section (matching photoai.com behavior)
      // They check if scrollTop > heroHeight
      const heroSection = document.querySelector('main > section');
      if (heroSection) {
        const heroHeight = heroSection.getBoundingClientRect().height;
        setShowStickyBar(window.scrollY > heroHeight);
      } else {
        // Fallback to fixed threshold if hero section not found
        setShowStickyBar(window.scrollY > 400);
      }
    };

    window.addEventListener("scroll", handleScroll);
    // Check on mount in case page loads scrolled
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle login/signup hash from navigation
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === "#login" || hash === "#signup" || hash === "#focus-email") {
      // Set mode based on hash
      if (hash === "#login") {
        setLoginMode('login');
      } else {
        setLoginMode('signup');
      }
      // Remove hash from URL
      window.history.replaceState(null, "", window.location.pathname);
      // Scroll to top and focus email input
      window.scrollTo(0, 0);
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 100);
    }
  }, []);

  const handleCTAClick = (mode: 'login' | 'signup') => {
    setLoginMode(mode);
    // Scroll to top and focus email input (matching photoai.com behavior)
    window.scrollTo(0, 0);
    setTimeout(() => {
      emailInputRef.current?.focus();
    }, 100);
  };

  const handleMagicLinkSubmit = async (email: string, captchaToken?: string) => {
    // Note: You may want to pass captchaToken to your API for verification
    // For now, we'll just use it for client-side validation
    await requestMagicLink(email);
  };

  return (
    <div className={cn("min-h-screen", theme.colors.backgroundCream)}>
      <SharedHeader onCTAClick={handleCTAClick} />

      {/* Hero Section */}
      <main className="pt-24 pb-16">
        <section className="mx-auto max-w-7xl px-6 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Hero Copy */}
            <div>
              <div className="flex flex-wrap gap-3 mb-6">
                <div className={cn(
                  "inline-block px-4 py-2 rounded-full text-sm font-semibold",
                  theme.colors.warmYellow,
                  theme.borders.neubrutalism,
                  theme.shadows.neubrutalismSm
                )}>
                  🎧 AI-Powered Podcast Listening
                </div>
                <div className={cn(
                  "inline-block px-4 py-2 rounded-full text-sm font-semibold",
                  theme.colors.primaryOrange,
                  theme.borders.neubrutalism,
                  theme.shadows.neubrutalismSm
                )}>
                  🌍 Auto-Translation in Any Language
                </div>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 text-gray-900 dark:text-white leading-tight">
                Ask Your Podcast Anything.
                <br />
                <span className={theme.text.primaryOrange}>In Any Language.</span>
              </h1>

              <p className="text-xl md:text-2xl mb-6 text-gray-700 dark:text-gray-300">
                Stop rewinding. Stop Googling terms you didn't catch. Chat directly with episodes and get answers <strong>from the show's perspective</strong>—not the internet's.
              </p>

              {/* Key Differentiator Callout */}
              <div className={cn(
                "p-5 mb-8 rounded-xl",
                theme.colors.primaryOrange,
                theme.borders.neubrutalism,
                theme.shadows.neubrutalism,
                "border-3"
              )}>
                <p className="text-base font-semibold text-gray-900 dark:text-gray-900">
                  <span className="text-lg mr-2">🎯</span>
                  Unlike ChatGPT, answers come from <span className="underline">your podcast's actual content</span>—not internet averages. Finally ask <em>"What would Lex Fridman say about X?"</em> and get a real answer.
                </p>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                ⚡ Set up in 60 seconds · Works with any RSS feed
              </p>
            </div>

            {/* Right: Email Form Card */}
            <div className={cn(
              "p-8 rounded-2xl",
              theme.cards.base,
              "max-w-md mx-auto lg:mx-0 lg:ml-auto w-full"
            )}>
              <h2 className="text-2xl font-black mb-2 text-gray-900 dark:text-white">
                {loginMode === 'login' ? 'Log In' : 'Get Started'}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                {loginMode === 'login' ? 'Enter your email to log in to your account' : 'Enter your email to get started'}
              </p>

              <EmailForm onSubmit={handleMagicLinkSubmit} inputRef={emailInputRef} mode={loginMode} />

              <div className="mt-6 pt-6 border-t-3 border-gray-900 dark:border-gray-300">
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  ✨ Cancel anytime · Pay only for what you use
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="mx-auto max-w-6xl px-6 py-16">
          <h2 className="text-3xl md:text-4xl font-black mb-12 text-center text-gray-900 dark:text-white">
            What Makes This Different
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Answers From The Source",
                description: "Unlike ChatGPT, responses come from your podcast's actual content. Ask 'What would this show say about X?' and get answers that reflect the host's real perspective and expertise.",
                icon: "🎯",
                highlight: true
              },
              {
                title: "Never Rewind Again",
                description: "Missed a detail? Forgot a name? Just ask. Get instant answers about episodes, concepts, and complex topics without scrubbing through audio.",
                icon: "💬"
              },
              {
                title: "Any Language, Your Language",
                description: "Access podcasts from anywhere in the world. Ask questions in your native language—even if the podcast is in Japanese, German, or Portuguese.",
                icon: "🌍"
              },
              {
                title: "Find Key Information",
                description: "Get quotes and references instantly. Perfect for researchers, students, and anyone taking notes.",
                icon: "🔍"
              },
              {
                title: "Set Up in 60 Seconds",
                description: "Chat with any podcast—just paste an RSS feed. Explore new shows instantly without subscribing to each one.",
                icon: "⚡"
              },
              {
                title: "New Episode Summaries",
                description: "Get AI-generated episode summaries delivered to your inbox when new episodes drop. Never miss a key insight.",
                icon: "📬",
                premium: true
              }
            ].map((feature, index) => (
              <div
                key={feature.title}
                className={cn(
                  "p-8 rounded-2xl relative",
                  feature.highlight ? theme.colors.primaryOrange : theme.cards.base,
                  "hover:-translate-y-1 transition-transform cursor-default",
                  index === 0 && "lg:col-span-2"
                )}
              >
                {'premium' in feature && feature.premium && (
                  <div className={cn(
                    "absolute -top-3 -right-3 px-3 py-1 rounded-full text-xs font-bold",
                    "bg-gradient-to-r from-violet-500 to-fuchsia-500",
                    "text-white border-2 border-violet-600 dark:border-violet-400",
                    theme.shadows.neubrutalismSm
                  )}>
                    Premium
                  </div>
                )}
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className={cn(
                  "text-xl font-bold mb-3",
                  feature.highlight ? "text-gray-900 dark:text-gray-900" : "text-gray-900 dark:text-white"
                )}>
                  {feature.title}
                </h3>
                <p className={cn(
                  feature.highlight 
                    ? "text-gray-900 dark:text-gray-900 font-medium" 
                    : "text-gray-700 dark:text-gray-300"
                )}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className="mx-auto max-w-4xl px-6 py-16">
          <h2 className="text-3xl md:text-4xl font-black mb-12 text-center text-gray-900 dark:text-white">
            How It Works
          </h2>

          <div className="space-y-8">
            {[
              {
                step: "1",
                title: "Paste Any RSS Feed",
                description: "Chat with any podcast. Just paste an RSS feed and start chatting after full transcription of every episode (8-24 hours)."
              },
              {
                step: "2",
                title: "Ask Anything",
                description: "Type your question in any language. Get answers with timestamps and exact quotes."
              },
              {
                step: "3",
                title: "Go Deeper",
                description: "Follow up, explore tangents, reference old episodes. Your podcast becomes searchable and conversational."
              }
            ].map((item) => (
              <div
                key={item.step}
                className={cn(
                  "flex gap-6 p-6 rounded-xl",
                  theme.cards.episode,
                  "hover:-translate-y-0.5 transition-transform"
                )}
              >
                <div className={cn(
                  "flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center text-2xl font-black",
                  theme.colors.primaryOrange,
                  theme.borders.neubrutalism,
                  theme.shadows.neubrutalismSm
                )}>
                  {item.step}
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                    {item.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases */}
        <section className="mx-auto max-w-4xl px-6 py-16">
          <div className={cn(
            "p-12 rounded-2xl text-center",
            theme.cards.base
          )}>
            <h2 className="text-2xl font-black mb-6 text-gray-900 dark:text-white">
              Perfect For
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                "History buffs following multi-part series",
                "Researchers taking notes on interviews",
                "Language learners accessing foreign podcasts",
                "Commuters who miss details on the go",
                "Superfans who want to search old episodes"
              ].map((useCase) => (
                <span
                  key={useCase}
                  className={cn(
                    "px-4 py-2 rounded-full text-sm font-medium",
                    "bg-white dark:bg-gray-800",
                    theme.borders.neubrutalism,
                    theme.shadows.neubrutalismSm
                  )}
                >
                  {useCase}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="mx-auto max-w-3xl px-6 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-6 text-gray-900 dark:text-white">
            Your Favorite Podcast, Now Searchable
          </h2>
          <p className="text-xl mb-8 text-gray-700 dark:text-gray-300">
            Chat with any podcast. Just paste an RSS feed and start asking questions.
          </p>
          <EmailForm onSubmit={handleMagicLinkSubmit} />
        </section>
      </main>

      {/* Footer */}
      <footer className={cn(
        "border-t-3 border-gray-900 dark:border-gray-300",
        theme.colors.cardCream
      )}>
        <div className="mx-auto max-w-6xl px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img
                  src={logoUrl}
                  alt="PodWise GPT"
                  className="h-8 w-auto"
                />
                <span className="font-bold text-lg text-gray-900 dark:text-white">PodWise GPT</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                AI-powered podcast engagement platform
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-3 text-gray-900 dark:text-white">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/pricing" className="text-gray-600 dark:text-gray-400 hover:underline">Pricing</Link></li>
                <li><Link to="/faq" className="text-gray-600 dark:text-gray-400 hover:underline">FAQ</Link></li>
                <li><Link to="/login" className="text-gray-600 dark:text-gray-400 hover:underline">Login</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-3 text-gray-900 dark:text-white">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:quiet.lens2873@fastmail.com" className="text-gray-600 dark:text-gray-400 hover:underline">Contact</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-3 text-gray-900 dark:text-white">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="text-gray-600 dark:text-gray-400 hover:underline">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-gray-600 dark:text-gray-400 hover:underline">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t-3 border-gray-900 dark:border-gray-300 text-center text-sm text-gray-600 dark:text-gray-400">
            © {new Date().getFullYear()} PodWise GPT. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Sticky Bottom Bar */}
      <StickyBottomBar visible={showStickyBar} onSubmit={handleMagicLinkSubmit} />
    </div>
  );
}
